import { Customer } from '../types/customer'
import { CustomerUser, CustomerUserTDO } from '../types/customer-user'
import { customerUserRoles } from '../utils/config'
import { graphQLClient } from './authService'
import customerUserMutations from './graphql/customerUsers/mutations'
import customerUserQueries from './graphql/customerUsers/queries'
import { Place } from '../types/utils'

const parseAssignedPlaces = (ids: string[]) => ids.map((id) => ({ id }))

class CustomerUserService {
  getAllFromCustomerId = async ({ id }: { id?: string }, params?: { [x: string]: any }): Promise<CustomerUser[]> => {
    const where = {
      customer: {
        id,
      },
    }

    if (params && params.locations && params.locations.length > 0) {
      Object.assign(where, {
        assignedPlaces_some: {
          id_in: params.locations?.map((place: Place) => place.id),
        },
      })
    }

    if (params && params.roles && params.roles.length > 0) {
      Object.assign(where, {
        role_in: params.roles,
      })
    }

    const response = await graphQLClient.request(customerUserQueries.getAll, {
      where,
      orderBy: 'createdAt_DESC',
    })

    return response.customerUsersConnection.edges.map((customerUser: { node: any }) => customerUser.node)
  }

  createCustomerUser = async ({
    places,
    displayName,
    email,
    role,
    customerId,
  }: CustomerUserTDO & { customerId?: string }): Promise<Customer> => {
    const assignedPlaces: { id: string }[] = places.length > 0 ? parseAssignedPlaces(places) : []

    const response = await graphQLClient.request(customerUserMutations.createCustomerUser, {
      data: {
        user: { createOrConnect: { email, displayName } },
        customer: {
          connect: {
            id: customerId,
          },
        },
        assignedPlaces:
          role === customerUserRoles.operator || role === customerUserRoles.manager
            ? { connect: assignedPlaces }
            : undefined,
        role,
      },
    })

    return response.createCustomerUser
  }

  updateCustomerUser = async ({
    id,
    places,
    displayName,
    email,
    role,
  }: CustomerUserTDO & { id: string }): Promise<CustomerUser> => {
    const assignedPlaces: { id: string }[] = places.length > 0 ? parseAssignedPlaces(places) : []

    const response = await graphQLClient.request(customerUserMutations.updateCustomerUser, {
      where: {
        id,
      },
      data: {
        role,
        user: {
          update: {
            displayName,
            email,
          },
        },
        assignedPlaces:
          role === customerUserRoles.operator || role === customerUserRoles.manager
            ? { set: assignedPlaces }
            : undefined,
      },
    })

    return response.updateCustomerUser
  }

  deleteCustomerUser = async (id: string): Promise<{ count: number }> => {
    const response = await graphQLClient.request(customerUserMutations.deleteCustomerUser, {
      where: {
        id_in: id,
      },
    })

    return response
  }
}

const customerUserService = new CustomerUserService()

export default customerUserService
